import { Controller } from '@hotwired/stimulus'

// Recolectar consumes real stock, so the control asks for a second of intent instead of a dialog:
// people learn to dismiss dialogs in a week, and a wet glove taps them by accident.
//
// The keyboard holds it too (Space or Enter), the fill is a progressbar with a live region, and a
// finger that drifts more than a few pixels is scrolling, not holding.
export default class extends Controller {
  static targets = ['button', 'fill', 'announcement']
  static values = {
    duration: { type: Number, default: 900 },
    moveThreshold: { type: Number, default: 10 },
    announcement: String
  }

  // 🔴 THE BUTTON SHIPS DISABLED AND THIS IS WHAT ENABLES IT. It is a real `type="submit"`, so
  // until this controller connects the only thing between a tap and consumed stock is
  // `click->swallow` — a handler that does not exist yet at that point. Failing closed costs one
  // line; the markup carries a fallback for the case where it never connects at all.
  connect () {
    // 🔴 NOT UNCONDITIONALLY. The markup ships the button `disabled` AND `data-blocked="true"`
    // when the shelf cannot cover the pick, and enabling it here removed the only guard that does
    // not depend on our own JavaScript. `start()` still refuses a blocked hold, so this never
    // opened the till — but a stock-consuming submit lost its last non-JS defence.
    this.buttonTarget.disabled = this.blocked
  }

  disconnect () {
    this.cancel()
  }

  // 🔴 `data-blocked` is the SCREEN's, not ours: it says the shelf cannot cover this pick. We own
  // only whether the control is WIRED. Both are consulted — `connect` leaves a blocked button
  // disabled, and this refuses the hold even if something else enabled it.
  start (event) {
    if (this.buttonTarget.disabled || this.blocked || this.timer) return

    this.origin = event && event.clientX != null ? { x: event.clientX, y: event.clientY } : null
    this.startedAt = Date.now()
    this.timer = setInterval(() => this.tick(), 60)
  }

  key (event) {
    if (event.key !== ' ' && event.key !== 'Enter') return

    // Held keys repeat; `start` is idempotent, and preventing the default stops the browser
    // firing its own click on release.
    event.preventDefault()
    this.start(null)
  }

  moved (event) {
    if (!this.timer || !this.origin) return

    const drift = Math.max(Math.abs(event.clientX - this.origin.x), Math.abs(event.clientY - this.origin.y))
    if (drift > this.moveThresholdValue) this.cancel()
  }

  // 🔴 A click NEVER submits, not even after a completed hold: `complete` submits the form
  // itself, and the pointerup that ends a successful hold fires one more click behind it. A flag
  // that let clicks through once held would also still be open on the screen a failed submit
  // leaves standing.
  swallow (event) {
    event.preventDefault()
  }

  cancel () {
    if (!this.timer) return

    clearInterval(this.timer)
    this.timer = null
    this.origin = null
    this.render(0)
  }

  tick () {
    const percent = Math.min(100, Math.round(((Date.now() - this.startedAt) / this.durationValue) * 100))
    this.render(percent)

    if (percent >= 100) this.complete()
  }

  complete () {
    this.cancel()
    this.buttonTarget.form.requestSubmit(this.buttonTarget)
  }

  get blocked () {
    return this.buttonTarget.dataset.blocked === 'true'
  }

  render (percent) {
    if (this.hasFillTarget) {
      this.fillTarget.style.width = `${percent}%`
      this.fillTarget.setAttribute('aria-valuenow', String(percent))
    }
    if (this.hasAnnouncementTarget) {
      this.announcementTarget.textContent = percent === 0
        ? ''
        : this.announcementValue.replaceAll('%{percent}', percent)
    }
  }
}
